import { logger } from 'monitor';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type AlertProps, Container, useTheme } from 'ui';

import { useCamera } from '../../hooks/useCamera/useCamera';
import { relativeZIndex } from '../../index';
import type { MaterialForm, PreviewGalleryImage } from '../../types';
import { captureImage, getAspectRatio } from '../../utils/captureImage/captureImage';
import { CameraFooter } from '../CameraFooter/CameraFooter';
import { CameraHeader } from '../CameraHeader/CameraHeader';
import { CountdownOverlay } from '../CountdownOverlay/CountdownOverlay';
import { Guide, type GuideProps } from '../Guide/Guide';
import { InfoText } from '../InfoText/InfoText';
import { PreviewImageReview } from '../PreviewImageReview/PreviewImageReview';
import { TiltOverlay } from '../TiltOverlay/TiltOverlay';
import { Video } from '../Video/Video';
import { VideoContainer } from '../VideoContainer/VideoContainer';

export interface CameraProps {
  handleCapture: (photo: File) => void;
  handleClose: () => void;
  handleOnComplete?: () => void;
  previewImages: PreviewGalleryImage[];
  handleDeletePreviewImage?: (image: PreviewGalleryImage) => void;
  showDeleteInPreview?: boolean;
  guideIllustrations: GuideProps['guideIllustrations'];
  materialForm: MaterialForm;
  showAerosolAnnotation?: boolean;
  showAerosolBreakdown?: boolean;
}

export const Camera = ({
  handleCapture,
  handleClose,
  handleOnComplete,
  previewImages,
  handleDeletePreviewImage,
  showDeleteInPreview = true,
  guideIllustrations,
  materialForm,
  showAerosolAnnotation = false,
  showAerosolBreakdown = false,
}: CameraProps) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<AlertProps['severity']>('info');
  const [selectedImage, setSelectedImage] = useState<PreviewGalleryImage | null>(null);
  const [actionDialogIsOpen, setActionDialogIsOpen] = useState(false);
  const [guideIsOpen, setGuideIsOpen] = useState(false);
  const [showCountdownOverlay, setShowCountdownOverlay] = useState(false);
  const alertTimerRef = useRef<NodeJS.Timeout | null>(null);
  const alertEndTimeRef = useRef<number>(0);
  const captureTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCapturingRef = useRef<boolean>(false);

  const {
    releaseWakeLock,
    autoCapturing,
    toggleAutoCapture,
    stream,
    releaseStream,
    countdown,
    shouldCapture,
    markCaptureTriggered,
    isLoading: cameraStreamIsLoading,
    isPortrait,
    isDeviceReady,
  } = useCamera({ materialForm });

  const theme = useTheme();

  const aspectRatio = getAspectRatio({ isPortrait, materialForm });

  const capture = useCallback(async () => {
    if (isCapturingRef.current) return;

    const video = document.querySelector('video') as HTMLVideoElement;
    if (!video) return;

    isCapturingRef.current = true;

    const capturedImage = await captureImage({ video });

    if (!capturedImage) {
      isCapturingRef.current = false;
      return;
    }

    setShowCountdownOverlay(true);
    handleCapture(capturedImage);
    setTimeout(() => {
      setShowCountdownOverlay(false);
      isCapturingRef.current = false;
    }, 1000);
  }, [handleCapture]);

  const captureDisabled = cameraStreamIsLoading || !isDeviceReady;
  const completeDisabled = previewImages.length === 0;

  useEffect(() => {
    if (shouldCapture && !isCapturingRef.current) {
      markCaptureTriggered();

      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
        captureTimeoutRef.current = null;
      }

      setShowCountdownOverlay(true);
      capture();
      captureTimeoutRef.current = setTimeout(() => {
        setShowCountdownOverlay(false);
        captureTimeoutRef.current = null;
      }, 1000);
    }
  }, [shouldCapture, capture, markCaptureTriggered]);

  const handleToggleAutoCapture = () => {
    const newState = !autoCapturing;
    toggleAutoCapture();
    flashAlert({
      message: newState ? 'Auto capture on' : 'Auto capture off',
      severity: 'info',
    });
  };

  const flashAlert = ({ message, severity = 'info' }: { message: string; severity?: AlertProps['severity'] }) => {
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
      alertTimerRef.current = null;
    }

    alertEndTimeRef.current = Date.now() + 1000;

    setShowAlert(true);
    setAlertMessage(message);
    setAlertSeverity(severity);

    alertTimerRef.current = setTimeout(() => {
      if (Date.now() >= alertEndTimeRef.current) {
        setShowAlert(false);
        alertTimerRef.current = null;
      }
    }, 1000);
  };

  const handlePreviewImageClick = (image: PreviewGalleryImage) => {
    logger.info('Previewing image', {
      image,
    });
    setSelectedImage(image);
    setActionDialogIsOpen(true);
  };

  const handleActionDialogClose = () => {
    setActionDialogIsOpen(false);
  };

  const deleteImage = () => {
    if (!selectedImage) return;
    handleDeletePreviewImage?.(selectedImage);
    handleActionDialogClose();
  };

  const handleOpenGuide = () => {
    setGuideIsOpen(true);
  };

  const handleCloseGuide = () => {
    setGuideIsOpen(false);
  };

  const [devicePositionAlertActive, setDevicePositionAlertActive] = useState(false);

  useEffect(() => {
    if (!isDeviceReady && !devicePositionAlertActive) {
      setDevicePositionAlertActive(true);
      setShowAlert(true);
      setAlertMessage('Keep device level and still');
      setAlertSeverity('warning');
    } else if (isDeviceReady && devicePositionAlertActive) {
      if (Date.now() >= alertEndTimeRef.current) {
        setDevicePositionAlertActive(false);
        setShowAlert(false);
      }
    }
  }, [devicePositionAlertActive, isDeviceReady]);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
      if (captureTimeoutRef.current) {
        clearTimeout(captureTimeoutRef.current);
      }
    };
  }, []);

  const handleStopCamera = () => {
    releaseWakeLock();
    releaseStream();
    handleClose();
  };

  return (
    <>
      <Container
        sx={{
          backgroundColor: theme.palette.secondary.main,
          display: 'flex',
          flexDirection: isPortrait ? 'column' : 'row',
          height: '100dvh',
          margin: 0,
          padding: '0 !important',
          position: 'fixed',
          width: '100dvw',
        }}
      >
        <CameraHeader
          autoCapturing={autoCapturing}
          toggleAutoCapture={handleToggleAutoCapture}
          handleStopCamera={handleStopCamera}
          isPortrait={isPortrait}
        />
        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.infoText}>
          <InfoText showAlert={showAlert} message={alertMessage} severity={alertSeverity} />
        </VideoContainer>
        {showCountdownOverlay || (autoCapturing && countdown > 0) ? (
          <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.countdownOverlay}>
            <CountdownOverlay countdown={showCountdownOverlay ? 0 : countdown} />
          </VideoContainer>
        ) : null}

        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.tiltIndicator}>
          <TiltOverlay isCameraLevel={isDeviceReady} />
        </VideoContainer>

        <VideoContainer isPortrait={isPortrait} aspectRatio={aspectRatio} zIndex={relativeZIndex.videoContainer}>
          <Video stream={stream} isLoading={cameraStreamIsLoading} />
        </VideoContainer>

        <CameraFooter
          capture={capture}
          captureDisabled={captureDisabled}
          completeDisabled={completeDisabled}
          slotProps={{
            previewGallery: {
              images: previewImages,
              isPortrait,
              onClick: handlePreviewImageClick,
            },
          }}
          handleOpenGuide={handleOpenGuide}
          handleOnComplete={handleOnComplete}
          isPortrait={isPortrait}
        />
      </Container>

      {selectedImage ? (
        <PreviewImageReview
          open={actionDialogIsOpen}
          onClose={handleActionDialogClose}
          selectedImage={selectedImage}
          onDeleteImage={deleteImage}
          showDeleteButton={showDeleteInPreview}
          isPortrait={isPortrait}
          showAerosolAnnotation={showAerosolAnnotation}
          showAerosolBreakdown={showAerosolBreakdown}
        />
      ) : null}

      <Guide open={guideIsOpen} onClose={handleCloseGuide} guideIllustrations={guideIllustrations} />
    </>
  );
};
